import React from 'react';
import { Link } from 'react-router-dom';
import { PackageOpen, ArrowUpRight, Info, Medal } from 'lucide-react';

import { productsApi } from '../lib/productsApi';
import PeriodPicker from '../components/products/PeriodPicker';
import ProductImage from '../components/products/ProductImage';
import EmptyState from '../components/common/EmptyState';

// -----------------------------------------------------------------------
// Skeleton for a product card while the list is loading.
// -----------------------------------------------------------------------
function CardSkeleton() {
    return (
        <div
            className="bg-white rounded-2xl border border-[#E5E7EB] p-4 flex flex-col gap-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
            data-testid="product-card-skeleton"
        >
            <div className="w-full aspect-[4/3] rounded-xl bg-[#F5F6FA] overflow-hidden relative">
                <span className="absolute inset-0 block animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent" />
            </div>
            <div className="h-4 w-3/4 bg-[#F5F6FA] rounded" />
            <div className="h-3 w-1/2 bg-[#F5F6FA] rounded" />
            <div className="mt-2 grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-10 bg-[#F5F6FA] rounded" />
                ))}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// A single metric with a tiny gauge. For "Total" we skip the bar since
// it's a count, not a rate.
// -----------------------------------------------------------------------
function Gauge({ pct, tone }) {
    const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
    return (
        <div
            className="h-1 w-full rounded-full bg-[#F1F2F7] overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(clamped)}
            aria-valuemin={0}
            aria-valuemax={100}
        >
            <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${clamped}%`, background: tone }}
            />
        </div>
    );
}

function Metric({ label, value, gauge, tone, isCount = false, testId }) {
    return (
        <div className="flex flex-col gap-1.5" data-testid={testId}>
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#6B7280] font-medium">{label}</div>
            <div className="text-[18px] font-bold text-[#1A1D2E] leading-none">
                {isCount ? Number(value).toLocaleString('fr-FR') : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%`}
            </div>
            {!isCount && <Gauge pct={gauge} tone={tone} />}
        </div>
    );
}

// -----------------------------------------------------------------------
// RankBadge — small medal for top-3, plain "#N" pill for #4+.
// Medals use the app's indigo theme paired with the traditional medal
// palette (gold #F59E0B / silver #94A3B8 / bronze #B45309). The overall
// look stays cohérent with the existing badge system (rounded pill,
// 20% background opacity, matching stroke).
// -----------------------------------------------------------------------
const MEDAL_TIERS = {
    1: {
        label: '#1',
        bg: 'linear-gradient(135deg, #FDE68A 0%, #F59E0B 100%)',
        text: '#78350F',
        ring: 'rgba(245, 158, 11, 0.35)',
        tone: 'gold',
    },
    2: {
        label: '#2',
        bg: 'linear-gradient(135deg, #F1F5F9 0%, #94A3B8 100%)',
        text: '#334155',
        ring: 'rgba(148, 163, 184, 0.35)',
        tone: 'silver',
    },
    3: {
        label: '#3',
        bg: 'linear-gradient(135deg, #FED7AA 0%, #B45309 100%)',
        text: '#7C2D12',
        ring: 'rgba(180, 83, 9, 0.35)',
        tone: 'bronze',
    },
};

function RankBadge({ rank }) {
    if (!rank) return null;
    const tier = MEDAL_TIERS[rank];
    if (tier) {
        return (
            <span
                data-testid={`rank-badge-${rank}`}
                data-tone={tier.tone}
                title={`Rang ${tier.label} par score`}
                className="inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-bold leading-none"
                style={{
                    background: tier.bg,
                    color: tier.text,
                    boxShadow: `0 0 0 1px ${tier.ring} inset, 0 1px 2px rgba(16,24,40,0.15)`,
                }}
            >
                <Medal size={13} strokeWidth={2.25} />
                {tier.label}
            </span>
        );
    }
    return (
        <span
            data-testid={`rank-badge-${rank}`}
            data-tone="plain"
            title={`Rang #${rank} par score`}
            className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold text-[#6B7280] bg-white/90 border border-[#E5E7EB] leading-none"
        >
            #{rank}
        </span>
    );
}

// -----------------------------------------------------------------------
// Product card — bento cell. Hover reveals a "Voir les détails →" overlay.
// The whole card is a <Link> so keyboard navigation works out of the box.
// -----------------------------------------------------------------------
function ProductCard({ p, rank }) {
    const s = p.stats || { total: 0, confirmation_rate: 0, delivery_rate: 0, return_rate: 0, score: 0 };
    const empty = !s.total;

    return (
        <Link
            to={`/produits/${p.id}`}
            data-testid={`product-card`}
            data-product-id={p.id}
            data-rank={empty ? '' : String(rank || '')}
            data-score={empty ? '' : String(s.score ?? 0)}
            className={
                'group relative bg-white rounded-2xl border border-[#E5E7EB] p-4 flex flex-col gap-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgba(75,63,216,0.35),0_4px_12px_-6px_rgba(75,63,216,0.15)] hover:border-[#C7C1F2] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4B3FD8]/25 ' +
                (empty ? ' opacity-60 grayscale-[0.15]' : '')
            }
        >
            {/* Image + top badges (rank on the left, Personnalisé still at top-left) */}
            <div className="relative w-full aspect-[4/3] overflow-hidden rounded-xl">
                <ProductImage src={p.image_url} alt={p.name} rounded="" />
                <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                    {!empty && rank && <RankBadge rank={rank} />}
                    {p.is_personalized && (
                        <span
                            data-testid="badge-personnalise"
                            className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold text-[#4B3FD8] bg-[#4B3FD8]/15 border border-[#4B3FD8]/20 backdrop-blur-sm leading-none"
                        >
                            Personnalisé
                        </span>
                    )}
                </div>
            </div>

            {/* Name */}
            <div className="min-h-[42px]">
                <h3
                    className="text-[14px] font-semibold text-[#1A1D2E] leading-snug line-clamp-2"
                    title={p.name}
                >
                    {p.name}
                </h3>
            </div>

            {/* Metrics */}
            {!empty ? (
                <div className="grid grid-cols-2 gap-4 pt-1">
                    <Metric testId="metric-total"       label="Commandes" value={s.total}             isCount />
                    <Metric testId="metric-conf"        label="Confirmé"   value={s.confirmation_rate} gauge={s.confirmation_rate} tone="#10B981" />
                    <Metric testId="metric-delivery"    label="Livraison"  value={s.delivery_rate}     gauge={s.delivery_rate}     tone="#3B82F6" />
                    <Metric testId="metric-return"      label="Retour"     value={s.return_rate}       gauge={s.return_rate}       tone="#EF4444" />
                </div>
            ) : (
                <div
                    data-testid="product-card-empty"
                    className="flex items-center gap-2 h-[76px] rounded-lg bg-[#F9FAFB] border border-dashed border-[#E5E7EB] px-3 text-[12px] text-[#6B7280]"
                >
                    <Info size={14} strokeWidth={1.75} className="text-[#9CA3AF]" />
                    <span>Aucune donnée sur cette période</span>
                </div>
            )}
            {/* 5th metric — Score (Phase 14.2). Rendered on EVERY card so
                the composite indicator stays visible; empty cards get an
                em-dash placeholder in the same neutral tone. */}
            <div
                data-testid="metric-score"
                className={
                    'flex items-center justify-between rounded-lg px-3 py-2 border ' +
                    (empty
                        ? 'bg-[#F9FAFB] border-[#E5E7EB]'
                        : 'bg-[#4B3FD8]/[0.06] border-[#4B3FD8]/15')
                }
            >
                <div className={
                    'text-[11px] uppercase tracking-[0.12em] font-semibold ' +
                    (empty ? 'text-[#9CA3AF]' : 'text-[#4B3FD8]')
                }>Score</div>
                <div
                    data-testid="metric-score-value"
                    className={
                        'text-[18px] font-bold leading-none tabular-nums ' +
                        (empty ? 'text-[#9CA3AF]' : 'text-[#4B3FD8]')
                    }
                >
                    {empty ? '—' : Math.round(Number(s.score) || 0).toLocaleString('fr-FR')}
                </div>
            </div>

            {/* Hover overlay — "Voir les détails →" */}
            <div
                data-testid="product-card-hover-cta"
                className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center py-3 rounded-b-2xl opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 transition-all duration-200"
                style={{
                    background: 'linear-gradient(0deg, rgba(255,255,255,0.98) 30%, rgba(255,255,255,0) 100%)',
                }}
            >
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#4B3FD8]">
                    Voir les détails
                    <ArrowUpRight size={14} strokeWidth={2.25} />
                </span>
            </div>
        </Link>
    );
}

// -----------------------------------------------------------------------
// Sort dropdown (Phase 14.2). Simple native <select> wrapped in the same
// pill container as PeriodPicker for visual continuity.
//
// Semantics:
//   - All options sort DESC, EXCEPT `return_rate` which sorts ASC
//     (low return = better). Empty rows always land at the bottom.
//   - The rank badge shown on cards is ALWAYS the score-based rank — we
//     never move medals when the user changes the sort order. Rationale:
//     the medals communicate an objective performance benchmark, so users
//     can spot inconsistencies (e.g. "#1 by score but sorted 5th by
//     conversion") and drill into them.
// -----------------------------------------------------------------------
const SORT_OPTIONS = [
    { key: 'score',              label: 'Score',       dir: 'desc' },
    { key: 'total',              label: 'Commandes',   dir: 'desc' },
    { key: 'confirmation_rate',  label: 'Taux Conf',   dir: 'desc' },
    { key: 'delivery_rate',      label: 'Taux Livr',   dir: 'desc' },
    { key: 'return_rate',        label: 'Taux Retour', dir: 'asc'  },
];

function SortPicker({ value, onChange }) {
    return (
        <div
            data-testid="products-sort"
            className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-lg h-10 pl-3 pr-1 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
        >
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#6B7280] font-medium">Trier par</span>
            <select
                data-testid="products-sort-select"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 bg-transparent text-sm font-medium text-[#1A1D2E] focus:outline-none pr-2 cursor-pointer"
            >
                {SORT_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key} data-testid={`products-sort-opt-${o.key}`}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

// -----------------------------------------------------------------------
// Sort helpers — pure functions so they're trivially testable.
// -----------------------------------------------------------------------
function buildRankByScore(products) {
    // Rank ONLY products that have activity on the period. Empty products
    // remain rank-less (falls back to '' via data-rank).
    const active = products
        .filter((p) => (p.stats?.total || 0) > 0)
        .slice()
        .sort((a, b) => {
            const sa = Number(a.stats?.score || 0);
            const sb = Number(b.stats?.score || 0);
            if (sa !== sb) return sb - sa;
            // stable tie-breaker: alphabetical
            return (a.name || '').localeCompare(b.name || '', 'fr');
        });
    const map = new Map();
    active.forEach((p, i) => map.set(p.id, i + 1));
    return map;
}

function sortProducts(products, sortKey) {
    const cfg = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
    const dirMul = cfg.dir === 'asc' ? 1 : -1;
    // Always push empty products (total === 0) to the end.
    const active = [];
    const empty = [];
    products.forEach((p) => {
        (p.stats?.total ? active : empty).push(p);
    });
    active.sort((a, b) => {
        const va = Number(a.stats?.[sortKey] || 0);
        const vb = Number(b.stats?.[sortKey] || 0);
        if (va !== vb) return (va - vb) * dirMul;
        return (a.name || '').localeCompare(b.name || '', 'fr');
    });
    // Empty rows are just alphabetical among themselves.
    empty.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
    return [...active, ...empty];
}

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------
export default function ProduitsPage() {
    const [period, setPeriod] = React.useState({ period: 'this_month' });
    const [sortKey, setSortKey] = React.useState('score');
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [data, setData] = React.useState({ products: [], total: 0 });

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await productsApi.list(period);
            setData(res);
        } catch (e) {
            setError(e.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [period]);

    React.useEffect(() => { load(); }, [load]);

    const total = data.total || 0;

    // Rank map is stable across sort changes (always by score).
    const rankByScore = React.useMemo(
        () => buildRankByScore(data.products || []),
        [data.products],
    );

    // Ordered list according to the active sort dropdown.
    const orderedProducts = React.useMemo(
        () => sortProducts(data.products || [], sortKey),
        [data.products, sortKey],
    );

    return (
        <div data-testid="produits-page" className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-[#1A1D2E]">Produits</h2>
                    <span
                        data-testid="products-total-badge"
                        className="inline-flex items-center h-6 px-2.5 rounded-full text-xs font-semibold bg-[#4B3FD8]/10 text-[#4B3FD8]"
                    >
                        {total.toLocaleString('fr-FR')}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <PeriodPicker value={period} onChange={setPeriod} />
                    <SortPicker value={sortKey} onChange={setSortKey} />
                </div>
            </div>

            {error && (
                <div data-testid="products-error" className="bg-white border border-[#EF4444]/30 rounded-xl px-4 py-3 text-sm text-[#EF4444]">
                    {typeof error === 'string' ? error : 'Erreur lors du chargement des produits.'}
                </div>
            )}

            {loading && !data.products.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
                </div>
            ) : data.products.length === 0 ? (
                <EmptyState
                    testId="products-empty"
                    Icon={PackageOpen}
                    title="Aucun produit"
                    description="Le catalogue est vide. Les images produits seront ajoutées automatiquement à la réception des commandes Shopify."
                />
            ) : (
                <div
                    data-testid="products-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                >
                    {orderedProducts.map((p) => (
                        <ProductCard key={p.id} p={p} rank={rankByScore.get(p.id)} />
                    ))}
                </div>
            )}
        </div>
    );
}
